import React, { Fragment, useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Listbox, Transition } from '@headlessui/react';
import { cn } from '../../lib/utils';

export interface TailwindSelectOption {
  value: string | number | any; // Support objects as values (e.g., scenario/sensitivity objects)
  label: string;
  disabled?: boolean;
  icon?: string; // Material Symbols icon name
  isItalic?: boolean; // For "New Scenario", "No Sensitivity" etc.
  tooltip?: string; // Tooltip text for disabled items
}

export interface TailwindSelectProps {
  value: string | number | any; // Support objects as values
  onChange: (value: any) => void;
  options: TailwindSelectOption[];
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  error?: string;
  getOptionLabel?: (option: TailwindSelectOption) => string; // Custom label renderer
  autoWidth?: boolean; // Auto-size to fit longest option text
}

interface SelectBodyProps {
  open: boolean;
  buttonRef: React.RefObject<HTMLButtonElement>;
  selectedOption: TailwindSelectOption | undefined;
  options: TailwindSelectOption[];
  placeholder: string;
  disabled: boolean;
  error?: string;
  getOptionLabel?: (option: TailwindSelectOption) => string;
}

function SelectBody({ open, buttonRef, selectedOption, options, placeholder, disabled, error, getOptionLabel }: SelectBodyProps) {
  const [dropdownPosition, setDropdownPosition] = useState<{
    top?: number; bottom?: number; left: number; width: number; maxHeight: number;
  }>({ top: 0, left: 0, width: 0, maxHeight: 240 });

  // Update dropdown position when opened, flipping above if not enough space below
  useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const gap = 4;
      const preferredMaxHeight = 240;
      const spaceBelow = window.innerHeight - rect.bottom - gap;
      const spaceAbove = rect.top - gap;

      if (spaceBelow >= preferredMaxHeight || spaceBelow >= spaceAbove) {
        setDropdownPosition({
          top: rect.bottom + gap,
          bottom: undefined,
          left: rect.left,
          width: rect.width,
          maxHeight: Math.max(Math.min(preferredMaxHeight, spaceBelow), 100),
        });
      } else {
        const maxHeight = Math.max(Math.min(preferredMaxHeight, spaceAbove), 100);
        setDropdownPosition({
          top: undefined,
          bottom: window.innerHeight - rect.top + gap,
          left: rect.left,
          width: rect.width,
          maxHeight,
        });
      }
    }
  }, [open]);

  return (
    <div className="relative">
      <Listbox.Button
        ref={buttonRef}
        className={cn(
          'relative w-full appearance-none bg-slate-50 border text-slate-900 text-sm rounded-lg px-4 py-2.5 pr-10 font-medium text-left cursor-pointer transition-colors',
          'focus:ring-primary focus:border-primary',
          'hover:border-primary',
          error ? 'border-red-500' : 'border-slate-200',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <span className={cn('flex items-center gap-2', selectedOption?.isItalic && 'italic')}>
          {selectedOption?.icon && (
            <span className="material-symbols-outlined text-[20px]">{selectedOption.icon}</span>
          )}
          <span className="block truncate">
            {selectedOption ? (getOptionLabel ? getOptionLabel(selectedOption) : selectedOption.label) : placeholder}
          </span>
        </span>
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
          <span className="material-symbols-outlined text-slate-400 text-[20px]">expand_more</span>
        </span>
      </Listbox.Button>

      {/* Render dropdown in a portal at document root to avoid overflow issues */}
      {open && createPortal(
        <Transition
          as={Fragment}
          show={!!open}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options
            className="fixed overflow-auto rounded-lg bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
            style={{
              top: dropdownPosition.top !== undefined ? `${dropdownPosition.top}px` : 'auto',
              bottom: dropdownPosition.bottom !== undefined ? `${dropdownPosition.bottom}px` : 'auto',
              left: `${dropdownPosition.left}px`,
              minWidth: `${dropdownPosition.width}px`,
              width: 'max-content',
              maxHeight: `${dropdownPosition.maxHeight}px`,
              zIndex: 9999,
            }}
          >
            {options.map((option, index) => (
              <Listbox.Option
                key={typeof option.value === 'object' ? option.value?.id || index : option.value}
                value={option.value}
                disabled={option.disabled}
                className={({ active }) =>
                  cn(
                    'relative cursor-pointer select-none py-2 pr-4',
                    option.icon ? 'pl-4' : 'pl-10',
                    active ? 'bg-primary/10 text-primary' : 'text-slate-900',
                    option.disabled && 'opacity-50 cursor-not-allowed'
                  )
                }
                title={option.disabled && option.tooltip ? option.tooltip : undefined}
              >
                {({ selected, active }) => (
                  <>
                    <span className={cn(
                      'flex items-center gap-2',
                      selected && !option.icon ? 'font-semibold' : 'font-normal',
                      option.isItalic && 'italic'
                    )}>
                      {option.icon && (
                        <span className="material-symbols-outlined text-[20px]">{option.icon}</span>
                      )}
                      <span className="block whitespace-nowrap">{getOptionLabel ? getOptionLabel(option) : option.label}</span>
                    </span>
                    {selected && !option.icon && (
                      <span className={cn('absolute inset-y-0 left-0 flex items-center pl-3', active ? 'text-primary' : 'text-primary')}>
                        <span className="material-symbols-outlined text-[20px]">check</span>
                      </span>
                    )}
                  </>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>,
        document.body
      )}
    </div>
  );
}

export default function TailwindSelect({
  value,
  onChange,
  options,
  label,
  placeholder = 'Select an option',
  disabled = false,
  className,
  error,
  getOptionLabel,
  autoWidth = false,
}: TailwindSelectProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [contentMinWidth, setContentMinWidth] = useState(0);

  // Mesurer le texte le plus long pour auto-dimensionner le select
  useEffect(() => {
    if (autoWidth && measureRef.current) {
      let maxWidth = 0;
      const children = measureRef.current.children;
      for (let i = 0; i < children.length; i++) {
        const w = (children[i] as HTMLElement).scrollWidth;
        if (w > maxWidth) maxWidth = w;
      }
      // Ajouter le padding du bouton: px-4 (16px) + pr-10 (40px) + border (2px)
      setContentMinWidth(maxWidth + 58);
    }
  }, [autoWidth, options, getOptionLabel]);

  const selectedOption = options.find(option => {
    if (typeof option.value === 'object' && typeof value === 'object') {
      return option.value?.id === value?.id;
    }
    return option.value === value;
  });

  return (
    <div className={cn('flex flex-col gap-1.5', className)} style={autoWidth && contentMinWidth ? { minWidth: `${contentMinWidth}px` } : undefined}>
      {/* Élément caché pour mesurer la largeur des options */}
      {autoWidth && (
        <div
          ref={measureRef}
          aria-hidden
          style={{ position: 'fixed', visibility: 'hidden', height: 0, overflow: 'hidden', pointerEvents: 'none', top: -9999 }}
        >
          {options.map((opt, i) => (
            <div key={i} className="flex items-center gap-2 text-sm font-medium" style={{ whiteSpace: 'nowrap' }}>
              {opt.icon && <span className="material-symbols-outlined text-[20px]">{opt.icon}</span>}
              <span>{getOptionLabel ? getOptionLabel(opt) : opt.label}</span>
            </div>
          ))}
        </div>
      )}
      {label && (
        <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest px-1">
          {label}
        </label>
      )}

      <Listbox value={value} onChange={onChange} disabled={disabled}>
        {({ open }) => (
          <SelectBody
            open={open}
            buttonRef={buttonRef}
            selectedOption={selectedOption}
            options={options}
            placeholder={placeholder}
            disabled={disabled}
            error={error}
            getOptionLabel={getOptionLabel}
          />
        )}
      </Listbox>

      {error && (
        <p className="text-xs text-red-600 px-1">{error}</p>
      )}
    </div>
  );
}
